import React, { useState, useEffect } from 'react';
import './styles/tokens.css';
import { AppProvider, useAppContext } from './store/AppContext';
import { ToastProvider } from './components/ui';
import { authApi, isAuthenticated } from './api/client';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { WorkspacePage } from './pages/WorkspacePage';
import type { User } from './api/types';

// Inject pulse keyframe
const pulseStyle = `@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }`;

type Route = 'login' | 'register' | 'projects' | 'workspace';

function Router() {
  const { setUser, setActiveProjectId } = useAppContext();
  const [route, setRoute] = useState<Route>('login');
  const [bootstrapping, setBootstrapping] = useState(true);

  // Try to restore session from token
  useEffect(() => {
    if (!isAuthenticated()) {
      setBootstrapping(false);
      return;
    }
    authApi.me().then((res) => {
      if (res.ok) {
        setUser(res.data);
        setRoute('projects');
      }
      setBootstrapping(false);
    });
  }, [setUser]);

  const handleLogin = (user: User) => {
    setUser(user);
    setRoute('projects');
  };

  const handleLogout = () => {
    setUser(null);
    setActiveProjectId(null);
    setRoute('login');
  };

  const handleSelectProject = (id: string) => {
    setActiveProjectId(id);
    setRoute('workspace');
  };

  const handleBackToProjects = () => {
    setRoute('projects');
  };

  if (bootstrapping) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-secondary)',
          color: 'var(--text-muted)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (route === 'login') {
    return (
      <LoginPage
        onLogin={handleLogin}
        onGoRegister={() => setRoute('register')}
      />
    );
  }

  if (route === 'register') {
    return (
      <RegisterPage
        onRegister={handleLogin}
        onGoLogin={() => setRoute('login')}
      />
    );
  }

  if (route === 'projects') {
    return (
      <ProjectsPage
        onSelectProject={handleSelectProject}
        onLogout={handleLogout}
      />
    );
  }

  if (route === 'workspace') {
    return (
      <WorkspacePage
        onBack={handleBackToProjects}
        onLogout={handleLogout}
      />
    );
  }

  return null;
}

function App() {
  return (
    <>
      <style>{pulseStyle}</style>
      <AppProvider>
        <ToastProvider>
          <Router />
        </ToastProvider>
      </AppProvider>
    </>
  );
}

export default App;
